import { getToken, clearAuth } from './auth';

// Same-origin API calls prevent HTTPS mixed-content failures. The frontend
// nginx container proxies /api/* to the backend service.
const API_BASE = '';

function buildUrl(url) {
  if (!url) return API_BASE;
  if (url.startsWith('http://') || url.startsWith('https://')) return url;
  return `${API_BASE}${url}`;
}

async function parseResponse(response) {
  const contentType = response.headers.get('content-type') || '';
  const text = await response.text();

  if (!text) {
    return null;
  }

  if (contentType.includes('application/json')) {
    try {
      return JSON.parse(text);
    } catch {
      throw new Error('Server returned invalid JSON');
    }
  }

  try {
    return JSON.parse(text);
  } catch {
    return text;
  }
}

function getErrorMessage(method, url, response, data) {
  if (data && typeof data === 'object' && data.error) {
    return data.error;
  }

  if (typeof data === 'string' && data.trim()) {
    return data;
  }

  return `${method} ${url} failed with status ${response.status}`;
}

export async function apiFetch(url, options = {}) {
  const token = getToken();

  const headers = {
    ...(options.body ? { 'Content-Type': 'application/json' } : {}),
    ...(options.headers || {}),
  };

  if (token) {
    headers.Authorization = `Bearer ${token}`;
  }

  const response = await fetch(buildUrl(url), {
    ...options,
    headers,
  });

  const data = await parseResponse(response);

  if (response.status === 401) {
    clearAuth();
  }

  return {
    ok: response.ok,
    status: response.status,
    headers: response.headers,
    data,
  };
}

export async function authGet(url) {
  const res = await apiFetch(url, { method: 'GET' });
  if (!res.ok) {
    throw new Error(getErrorMessage('GET', url, res, res.data));
  }
  return res.data;
}

export async function authPost(url, body) {
  const res = await apiFetch(url, {
    method: 'POST',
    body: JSON.stringify(body),
  });

  if (!res.ok) {
    throw new Error(getErrorMessage('POST', url, res, res.data));
  }

  return res.data;
}

async function fetchBlob(url, options = {}, methodLabel = 'GET') {
  const token = getToken();
  const headers = {
    ...(options.body ? { 'Content-Type': 'application/json' } : {}),
    ...(options.headers || {}),
  };

  if (token) {
    headers.Authorization = `Bearer ${token}`;
  }

  const response = await fetch(buildUrl(url), {
    ...options,
    headers,
  });

  if (response.status === 401) {
    clearAuth();
  }

  if (!response.ok) {
    const errorText = await response.text();
    try {
      const errorJson = JSON.parse(errorText);
      throw new Error(errorJson.error || errorText || `${methodLabel} ${url} failed with status ${response.status}`);
    } catch (parseErr) {
      if (parseErr instanceof SyntaxError) {
        throw new Error(errorText || `${methodLabel} ${url} failed with status ${response.status}`);
      }
      throw parseErr;
    }
  }

  return response.blob();
}

export async function authGetBlob(url) {
  return fetchBlob(url, { method: 'GET' }, 'GET');
}

export async function authPostBlob(url, body) {
  return fetchBlob(url, {
    method: 'POST',
    body: JSON.stringify(body),
  }, 'POST');
}

export async function authPatch(url, body) {
  const res = await apiFetch(url, {
    method: 'PATCH',
    body: JSON.stringify(body),
  });

  if (!res.ok) {
    throw new Error(getErrorMessage('PATCH', url, res, res.data));
  }

  return res.data;
}

export async function authPut(url, body) {
  const res = await apiFetch(url, {
    method: 'PUT',
    body: JSON.stringify(body),
  });

  if (!res.ok) {
    throw new Error(getErrorMessage('PUT', url, res, res.data));
  }

  return res.data;
}

export async function authDelete(url, body = null) {
  const res = await apiFetch(url, {
    method: 'DELETE',
    ...(body ? { body: JSON.stringify(body) } : {}),
  });

  if (!res.ok) {
    throw new Error(getErrorMessage('DELETE', url, res, res.data));
  }

  return res.data;
}